import { useSessionKey } from "@auth/sessionStore";
import { kindLabel, type Entity } from "@entities/entitiesClient";
import { useEntityTypeahead } from "@entities/entitiesQueries";
import { faDeleteLeft } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CallButton } from "@phone/CallButton";
import {
  type ClientPhoneNumber,
  fetchClientPhoneNumbers,
  fetchPatientClientUuid,
  phoneNumberLabel,
} from "@phone/clientPhoneNumbers";
import { formatDialInput, toDestinationE164 } from "@phone/dialNumber";
import { formatPhoneNumber } from "@phone/phoneSetupClient";
import {
  Button,
  cn,
  EmptyNote,
  Input,
  PaperPanel,
} from "@scribenote/brian-design-system";
import { useQuery } from "@tanstack/react-query";
import { useId, useState } from "react";

// The outbound number contract does not support * or #.
const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"];

/** Shorter input is still being typed and gets no complaint. */
const looksComplete = (typed: string): boolean =>
  typed.startsWith("+")
    ? typed.replace(/\D/g, "").length >= 8
    : typed.replace(/\D/g, "").length >= 10;

const PHONE_INPUT = /^[+\d\s().-]*$/;

export const DialPadCard = ({
  onPlaced,
  className,
}: {
  /** A host showing the pad in a dialog closes it so the corner's panel is reachable. */
  onPlaced?: () => void;
  className?: string;
}) => {
  const feedbackId = useId();
  const [feedbackContainer, setFeedbackContainer] =
    useState<HTMLDivElement | null>(null);
  const [typed, setTyped] = useState("");
  const [picked, setPicked] = useState<Entity | null>(null);
  const [selectedNumberId, setSelectedNumberId] = useState<string | null>(null);
  const phoneInput = PHONE_INPUT.test(typed);
  const e164 = !picked && phoneInput ? toDestinationE164(typed) : null;
  const invalid =
    !picked &&
    phoneInput &&
    typed !== "" &&
    e164 === null &&
    looksComplete(typed);
  const numbers = useDialerNumbers(picked);
  const availableNumbers = numbers.data ?? [];
  const selectedNumber = selectedNumberId
    ? availableNumbers.find((number) => number.id === selectedNumberId)
    : (availableNumbers.find((number) => number.isPrimary) ??
      availableNumbers[0]);

  const changeInput = (value: string) => {
    setTyped(PHONE_INPUT.test(value) ? formatDialInput(value) : value);
    setPicked(null);
    setSelectedNumberId(null);
  };
  const press = (key: string) => changeInput(picked ? key : typed + key);
  const erase = () =>
    changeInput(
      phoneInput
        ? typed.replace(/[\s().-]+$/, "").slice(0, -1)
        : Array.from(typed).slice(0, -1).join(""),
    );

  return (
    <PaperPanel
      variant="card"
      className={cn("flex flex-col gap-3 p-4", className)}
      data-testid="dial-pad"
    >
      <Input
        label="Who to call"
        autoComplete="off"
        autoFocus
        placeholder="Phone number, client, or patient name"
        value={typed}
        onChange={(event) => changeInput(event.target.value)}
        error={invalid ? "That isn't a number Brian can dial." : undefined}
        aria-describedby={feedbackId}
        data-dd-privacy="hidden"
        data-testid="dial-pad-search"
      />
      <div
        id={feedbackId}
        ref={setFeedbackContainer}
        className="empty:hidden"
      />
      {picked ? (
        <OwnerNumbers
          entity={picked}
          numbers={numbers}
          selectedId={selectedNumber?.id}
          onSelect={setSelectedNumberId}
          onClear={() => {
            setPicked(null);
            setSelectedNumberId(null);
          }}
        />
      ) : !phoneInput ? (
        <NameResults
          query={typed}
          onPick={(entity) => {
            setPicked(entity);
            setTyped(entity.name);
            setSelectedNumberId(null);
          }}
        />
      ) : null}

      <div className="flex flex-col gap-3">
        <div className="flex justify-center">
          <div
            className="grid shrink-0 grid-cols-3 gap-2 self-center"
            aria-label="Dial pad"
            role="group"
          >
            {KEYS.map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => press(key)}
                className={cn(
                  "size-12 cursor-pointer rounded-full border border-rule bg-card font-mono text-lg text-ink",
                  "transition duration-150 ease-paper hover:-translate-y-px hover:shadow-lift-lg",
                  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
                  key === "0" && "col-start-2",
                )}
                data-testid={`dial-pad-key-${key}`}
              >
                {key}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-start justify-between gap-2 border-t border-rule pt-3">
          <CallButton
            destination={
              picked
                ? selectedNumber && !numbers.isError
                  ? {
                      kind: "client_number",
                      clientPhoneNumberUuid: selectedNumber.id,
                    }
                  : null
                : e164
                  ? { kind: "number", e164 }
                  : null
            }
            missingReason=""
            feedbackContainer={feedbackContainer}
            variant="primary"
            size="sm"
            buttonClassName="disabled:border-transparent disabled:opacity-60 disabled:grayscale"
            testId="dial-pad-call"
            onAdmitted={onPlaced}
          />
          <Button
            aria-label={
              phoneInput ? "Delete last digit" : "Delete last character"
            }
            variant="ghost"
            size="sm"
            className="text-annotation disabled:opacity-60 disabled:grayscale"
            disabled={typed === ""}
            onClick={erase}
            data-testid="dial-pad-erase"
          >
            <FontAwesomeIcon icon={faDeleteLeft} aria-hidden="true" />
            Delete
          </Button>
        </div>
      </div>
    </PaperPanel>
  );
};

/** A patient row names its owner: the owner is who gets called. */
const NameResults = ({
  query,
  onPick,
}: {
  query: string;
  onPick: (entity: Entity) => void;
}) => {
  const { data, ready, isFetching } = useEntityTypeahead(query, [
    "client",
    "patient",
  ]);
  if (!ready) return null;
  const results = data ?? [];
  if (results.length === 0) {
    return (
      <EmptyNote>
        {isFetching ? "Looking…" : `Nobody named “${query.trim()}” on file.`}
      </EmptyNote>
    );
  }
  return (
    <ul
      className="flex max-h-56 flex-col gap-1 overflow-y-auto"
      aria-label="Matching clients and patients"
      data-dd-privacy="hidden"
      data-testid="dial-pad-results"
    >
      {results.slice(0, 8).map((entity) => (
        <li key={entity.id}>
          <button
            type="button"
            onClick={() => onPick(entity)}
            className={cn(
              "flex w-full cursor-pointer items-baseline justify-between gap-3 rounded-paper px-2.5 py-1.5 text-left",
              "transition duration-150 ease-paper hover:bg-recess",
              "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
            )}
            data-testid={`dial-pad-result-${entity.kind}`}
          >
            <span className="truncate text-sm font-medium text-ink">
              {entity.name}
              {entity.kind === "patient" && entity.clientName && (
                <span className="font-normal text-ink-body">
                  {" "}
                  · {entity.clientName}
                </span>
              )}
            </span>
            <span className="shrink-0 text-annotation-caps text-annotation">
              {kindLabel(entity)}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
};

const useDialerNumbers = (entity: Entity | null) => {
  const sessionKey = useSessionKey();
  return useQuery({
    queryKey: ["dial-pad-owner-numbers", sessionKey, entity?.id],
    enabled: entity !== null,
    queryFn: async (): Promise<ClientPhoneNumber[]> => {
      if (!entity) return [];
      const clientUuid =
        entity.kind === "client"
          ? entity.id
          : await fetchPatientClientUuid(entity.id);
      return clientUuid ? fetchClientPhoneNumbers(clientUuid) : [];
    },
  });
};

const OwnerNumbers = ({
  entity,
  numbers,
  selectedId,
  onSelect,
  onClear,
}: {
  entity: Entity;
  numbers: ReturnType<typeof useDialerNumbers>;
  selectedId?: string;
  onSelect: (id: string) => void;
  onClear: () => void;
}) => (
  <div className="flex flex-col gap-2" data-testid="dial-pad-owner">
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-sm text-ink-body" data-dd-privacy="hidden">
        Numbers for{" "}
        <span className="font-medium text-ink">
          {entity.kind === "patient" && entity.clientName
            ? entity.clientName
            : entity.name}
        </span>
      </span>
      <button
        type="button"
        onClick={onClear}
        className="shrink-0 cursor-pointer whitespace-nowrap text-fine-print text-annotation underline decoration-dotted"
        data-testid="dial-pad-owner-clear"
      >
        search again
      </button>
    </div>
    {numbers.isPending ? (
      <span className="text-fine-print text-annotation">Looking…</span>
    ) : numbers.isError ? (
      <div className="flex items-center gap-2">
        <span className="text-fine-print text-ink-stopped">
          Brian couldn't read their numbers.
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void numbers.refetch()}
        >
          Try again
        </Button>
      </div>
    ) : numbers.data.length === 0 ? (
      <EmptyNote>
        No number on file. Enter a phone number in the field above.
      </EmptyNote>
    ) : (
      <ul
        className="flex flex-col gap-1"
        aria-label="Available phone numbers"
        data-dd-privacy="hidden"
      >
        {numbers.data.map((number) => (
          <li key={number.id}>
            <button
              type="button"
              onClick={() => onSelect(number.id)}
              aria-pressed={number.id === selectedId}
              className={cn(
                "flex w-full cursor-pointer items-center justify-between gap-3 rounded-paper border px-2.5 py-1.5 text-left focus-visible:outline-2 focus-visible:outline-accent",
                number.id === selectedId
                  ? "border-accent bg-accent/5"
                  : "border-rule hover:bg-smudge",
              )}
            >
              <span className="flex flex-col">
                <span className="font-mono text-sm text-ink">
                  {formatPhoneNumber(number.numberE164)}
                </span>
                <span className="text-fine-print text-annotation">
                  {phoneNumberLabel(number.label)}
                  {number.isPrimary && " · primary"}
                </span>
              </span>
              {number.id === selectedId && (
                <span className="text-fine-print text-accent">Selected</span>
              )}
            </button>
          </li>
        ))}
      </ul>
    )}
  </div>
);
